import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { 
  FaHotel, 
  FaThumbsUp, 
  FaComment, 
  FaBell, 
  FaCalendarCheck, 
  FaMoneyCheckAlt, 
  FaCheckDouble,
  FaArrowRight,
  FaShieldAlt
} from "react-icons/fa";
import { getUserNotifications, markNotificationAsRead } from "../services/notificationService";
import "./Notification.css";
import NavUser from "../components/NavUser";

const Notification = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    getUserNotifications(userId)
      .then((res) => setNotifications(res.data))
      .catch((err) => console.error("Failed to fetch notifications", err));
  }, [userId]);

  // Map notification type to icon
  const getIcon = (type) => {
    switch (type) {
      case "listing_created":
        return <FaHotel className="notif-icon-style listing-created" />;
      case "like":
        return <FaThumbsUp className="notif-icon-style like" />;
      case "comment":
        return <FaComment className="notif-icon-style comment" />;
      case "booking_created":
        return <FaCalendarCheck className="notif-icon-style booking-created" />;
      case "tour_booking_paid":
      case "hotel_booking_paid":
        return <FaMoneyCheckAlt className="notif-icon-style booking-paid" />;
      case "account_status_change":
        return <FaShieldAlt className="notif-icon-style account-status" />;
      default:
        return <FaBell className="notif-icon-style default" />;
    }
  };

  // Click handler to mark as read and navigate
  const handleClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await markNotificationAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
      }

      // Actionable routing based on notification type
      if (
        ["booking_created", "tour_booking_paid", "hotel_booking_paid"].includes(
          notification.type
        )
      ) {
        navigate("/bookings");
      } else if (["like", "comment", "mention"].includes(notification.type)) {
        navigate("/messages");
      } else if (notification.type === "listing_created") {
        navigate("/profile?tab=hotel");
      }
    } catch (err) {
      console.error("Notification click error", err);
    }
  };

  // Mark all notifications as read at once
  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter((n) => !n.is_read);
    if (unreadNotifications.length === 0) {
      Swal.fire({
        title: "Info",
        text: "You have no unread notifications.",
        icon: "info",
        confirmButtonColor: "#3b82f6"
      });
      return;
    }

    try {
      await Promise.all(unreadNotifications.map((n) => markNotificationAsRead(n.id)));
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      
      Swal.fire({
        title: "Success",
        text: "All notifications marked as read successfully!",
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Failed to mark all as read:", err);
      Swal.fire("Error", "Failed to mark all notifications as read.", "error");
    }
  };

  // Filter notifications into categories
  const displayedNotifications = notifications.filter((n) => {
    if (activeTab === "all") return true;
    if (activeTab === "bookings") {
      return ["booking_created", "tour_booking_paid", "hotel_booking_paid"].includes(n.type);
    }
    if (activeTab === "social") {
      return ["like", "comment", "mention"].includes(n.type);
    }
    if (activeTab === "system") {
      return ["listing_created", "account_status_change"].includes(n.type);
    }
    return true;
  });

  return (
    <div className="notif-page-bg">
      <NavUser />
      <div className="notification-container premium-glass">
        {/* Header Section */}
        <div className="notification-header-wrapper">
          <div className="notification-title-area">
            <h2 className="notification-header">Notification Center</h2>
            <p className="notification-subtitle">
              Stay updated with your bookings, interactions, and system alerts
            </p>
          </div>
          <button 
            className="mark-all-read-btn" 
            onClick={handleMarkAllAsRead}
            title="Mark all as read"
          >
            <FaCheckDouble /> Mark All Read
          </button>
        </div>

        {/* Categories Tab Navigation */}
        <div className="notification-tabs-premium">
          <button
            className={`tab-btn-premium ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All <span className="tab-badge">{notifications.length}</span>
          </button>
          <button
            className={`tab-btn-premium ${activeTab === "bookings" ? "active" : ""}`}
            onClick={() => setActiveTab("bookings")}
          >
            Bookings <span className="tab-badge">{notifications.filter(n => ["booking_created", "tour_booking_paid", "hotel_booking_paid"].includes(n.type)).length}</span>
          </button>
          <button
            className={`tab-btn-premium ${activeTab === "social" ? "active" : ""}`}
            onClick={() => setActiveTab("social")}
          >
            Social <span className="tab-badge">{notifications.filter(n => ["like", "comment", "mention"].includes(n.type)).length}</span>
          </button>
          <button
            className={`tab-btn-premium ${activeTab === "system" ? "active" : ""}`}
            onClick={() => setActiveTab("system")}
          >
            System <span className="tab-badge">{notifications.filter(n => ["listing_created", "account_status_change"].includes(n.type)).length}</span>
          </button>
        </div>

        {/* Notifications Listing */}
        {displayedNotifications.length === 0 ? (
          <div className="empty-notifications-card">
            <FaBell className="bell-empty-icon" />
            <h3>No Notifications Available</h3>
            <p>You are completely caught up! We will let you know when something new arrives.</p>
          </div>
        ) : (
          <div className="notification-list-premium">
            {displayedNotifications.map((n) => (
              <div
                key={n.id}
                className={`notification-card-premium ${n.is_read ? "read" : "unread"}`}
                onClick={() => handleClick(n)}
              >
                <div className="notif-icon-container">{getIcon(n.type)}</div>

                <div className="notif-body-container">
                  <div className="notif-top-row">
                    <span className={`notif-category-badge ${n.type}`}>
                      {n.type.replace("_", " ").toUpperCase()}
                    </span>
                    <span className="notif-time-stamp">
                      {new Date(n.created_at).toLocaleString()}
                    </span>
                  </div>

                  <div className="notif-message-text">{n.content}</div>
                  
                  {/* Action Link Indicator */}
                  <div className="notif-action-link">
                    <span>View details</span>
                    <FaArrowRight className="arrow-icon" />
                  </div>
                </div>
                
                {/* Unread glow indicator dot */}
                {!n.is_read && <span className="unread-glow-dot"></span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notification;
